import type { ECharts } from "echarts/core";
import type { JSX } from "react";
import { layoutTextMeasurer } from "@/utils/text";
import { useDataViewerStore } from "../../data-viewer-provider";
import { formatTooltipValue } from "../../format";
import {
    HOVER_LABEL_BORDER_PX,
    HOVER_LABEL_FONT,
    HOVER_LABEL_FONT_SIZE_PX,
    HOVER_LABEL_FONT_WEIGHT,
    HOVER_LABEL_PADDING_X_PX,
    HOVER_LABEL_RADIUS_PX,
    HOVER_LABEL_TEXT_COLOR,
} from "../layout/plot-geometry";
import { TAG_MIN_GAP_PX } from "../plan-panel-chrome";
import { type PreparedSeries, staleAfterMsFor } from "../prepare-series";
import { stackTagOffsets } from "./stack-tags";
import { findNearestReading } from "./value-at-time";

/** Tags are read in a stack of twenty, so they carry their series colour as a tint, not a fill. */
const TAG_FILL_PERCENT = 16;
const TAG_BORDER_PERCENT = 38;
/** Gap between a dot and the tag beside it. */
const TAG_GAP_PX = 8;
const TAG_BOX_EXTRA_PX = 2 * (HOVER_LABEL_PADDING_X_PX + HOVER_LABEL_BORDER_PX);

type Side = "left" | "right";

/** A series' dot at the hovered instant, plus the value tag beside it when tags are on. */
type Mark = {
    streamId: number;
    color: string;
    text: string;
    dotX: number;
    dotY: number;
    side: Side;
    tagY: number | null;
};

function tagWidth(text: string): number {
    return layoutTextMeasurer(text, HOVER_LABEL_FONT) + TAG_BOX_EXTRA_PX;
}

/**
 * Alternating sides down the panel gives the tags two columns to spread into, so series whose
 * values sit close together stop competing for one strip. A tag with no room on its side takes
 * the other.
 */
function sideFor(dotX: number, text: string, index: number, panelWidthPx: number): Side {
    const width = tagWidth(text);
    const fitsLeft = dotX - TAG_GAP_PX - width >= 0;
    const fitsRight = dotX + TAG_GAP_PX + width <= panelWidthPx;

    if (index % 2 === 0) return fitsLeft || !fitsRight ? "left" : "right";
    return fitsRight || !fitsLeft ? "right" : "left";
}

function buildMarks(
    chart: ECharts,
    series: PreparedSeries[],
    timeMs: number,
    plot: { topPx: number; bottomPx: number; widthPx: number; hoverTags: boolean },
): Mark[] {
    const candidates = series.flatMap((entry) => {
        const reading = findNearestReading(
            entry.points,
            timeMs,
            entry.seriesType,
            staleAfterMsFor(entry.axisUnitCategory),
        );
        if (!reading) return [];

        // By id, matching how buildTimePanelOptions binds the series. Addressing it by index would
        // hold only while the yAxis array happens to be in spec.axes order.
        const pixel = chart.convertToPixel({ xAxisIndex: 0, yAxisId: entry.axisId }, [reading.timeMs, reading.value]);
        if (!Array.isArray(pixel) || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) return [];

        // A snapped reading can sit outside the window when zoomed in.
        if (!chart.containPixel({ gridIndex: 0 }, pixel as [number, number])) return [];

        return [
            {
                streamId: entry.streamId,
                color: entry.color,
                text: formatTooltipValue(reading.value),
                // Subpixel values make this look like an oval rather than a circle
                dotX: Math.round(pixel[0]),
                dotY: Math.round(pixel[1]),
            },
        ];
    });

    const sided = [...candidates]
        .sort((a, b) => a.dotY - b.dotY)
        .map((mark, index) => ({ ...mark, side: sideFor(mark.dotX, mark.text, index, plot.widthPx) }));

    if (!plot.hoverTags) return sided.map((mark) => ({ ...mark, tagY: null }));

    const columns = (["left", "right"] as const).map((side) =>
        stackTagOffsets(
            sided.filter((mark) => mark.side === side).map((mark) => ({ item: mark, y: mark.dotY })),
            { minGapPx: TAG_MIN_GAP_PX, topPx: plot.topPx, bottomPx: plot.bottomPx },
        ),
    );

    // All or nothing: a column the plot can't separate would leave half the series tagged. The
    // chrome plan now sizes for the worst case — every tag in one column — so a column should
    // never actually come back null; this is a guard, not a path expected to run.
    if (columns.some((column) => column === null)) return sided.map((mark) => ({ ...mark, tagY: null }));

    return columns.flatMap((column) => (column ?? []).map(({ item, y }) => ({ ...item, tagY: y })));
}

export function HoverValues({
    chart,
    series,
    plotTopPx,
    plotBottomPx,
    panelWidthPx,
    hoverTags,
}: {
    chart: ECharts | null;
    series: PreparedSeries[];
    plotTopPx: number;
    plotBottomPx: number;
    panelWidthPx: number;
    hoverTags: boolean;
}): JSX.Element | null {
    // HoverTimeSlice, written by whichever panel is under the pointer (crosshair/use-hover-time.ts).
    // It holds the raw pointer time, which is also what the tooltip picks its rows from.
    const timeMs = useDataViewerStore((state) => state.timestamp);

    // A panel mounting mid-hover has no instance for this render and draws on the next pointer move.
    if (timeMs === null || !chart) return null;

    const marks = buildMarks(chart, series, timeMs, {
        topPx: plotTopPx,
        bottomPx: plotBottomPx,
        widthPx: panelWidthPx,
        hoverTags,
    });
    return (
        <div className={"pointer-events-none absolute inset-0"}>
            {marks.map((mark) => {
                const onRight = mark.side === "right";

                return (
                    <div key={mark.streamId}>
                        <div
                            className={"absolute z-1 size-[6px] -translate-1/2 rounded-full ring-2 ring-background"}
                            style={{ left: mark.dotX, top: mark.dotY, backgroundColor: mark.color }}
                        />
                        {mark.tagY !== null && (
                            <div
                                className={"absolute z-2 flex items-center leading-none whitespace-nowrap tabular-nums"}
                                style={{
                                    left: mark.dotX + (onRight ? TAG_GAP_PX : -TAG_GAP_PX),
                                    top: mark.tagY,
                                    transform: onRight ? "translateY(-50%)" : "translate(-100%, -50%)",
                                    // Height, not padding: TAG_MIN_GAP_PX is what the fit test in
                                    // planPanelChrome reserved, so a taller tag would overlap.
                                    height: TAG_MIN_GAP_PX,
                                    paddingInline: HOVER_LABEL_PADDING_X_PX,
                                    fontSize: HOVER_LABEL_FONT_SIZE_PX,
                                    fontWeight: HOVER_LABEL_FONT_WEIGHT,
                                    borderRadius: HOVER_LABEL_RADIUS_PX,
                                    backgroundColor: `color-mix(in srgb, ${mark.color} ${TAG_FILL_PERCENT}%, white)`,
                                    border: `${HOVER_LABEL_BORDER_PX}px solid color-mix(in srgb, ${mark.color} ${TAG_BORDER_PERCENT}%, white)`,
                                    color: HOVER_LABEL_TEXT_COLOR,
                                }}
                            >
                                {mark.text}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
